import React from 'react';
import { observer } from 'mobx-react-lite';
import type { LineElement, Point } from '@/types';

import { getStrokeDashArray, getStrokeLineCap } from '@/utils/strokeStyles';
import { generateSmoothPath, calculateMiddleControlPoint } from '@/utils/bezier';

interface LineProps {
  element: LineElement;
  isSelected: boolean;
}

export const Line: React.FC<LineProps> = observer(({ element, isSelected }) => {
  const { style } = element;
  
  // Support both new points array and legacy startPoint/endPoint
  let points: Point[] = [];
  if (element.points && element.points.length >= 2) {
    points = element.points;
    
    // If we only have 2 points and they're the same, don't render
    if (points.length === 2 && points[0] && points[1] &&
        points[0].x === points[1].x && 
        points[0].y === points[1].y) {
      return null;
    }
  } else if (element.startPoint && element.endPoint) {
    // Legacy support - convert to 3-point bezier with middle control point
    const middleControl = calculateMiddleControlPoint(
      element.startPoint, 
      element.endPoint, 
      20 // slight curve offset
    );
    points = [element.startPoint, middleControl, element.endPoint];
  } else {
    // Invalid line data - don't render anything
    console.warn('Line element has invalid point data:', element);
    return null;
  }

  // Generate smooth bezier path
  const smoothPath = generateSmoothPath(points);
  
  return (
    <g>
      <path
        d={smoothPath}
        fill="none"
        stroke={style.strokeColor}
        strokeWidth={style.strokeWidth}
        strokeDasharray={getStrokeDashArray(style.strokeStyle, style.strokeWidth)}
        opacity={style.opacity}
        strokeLinecap={getStrokeLineCap(style.strokeStyle)}
        strokeLinejoin="round"
      />
      {isSelected && (
        <>
          {/* Selection highlight */}
          <path
            d={smoothPath}
            fill="none"
            stroke="#007acc"
            strokeWidth={style.strokeWidth + 4}
            opacity={0.3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Control point handles */}
          {points.map((point, index) => (
            <circle
              key={`control-${index}`}
              cx={point.x}
              cy={point.y}
              r={4}
              fill={index === 0 || index === points.length - 1 ? "#007acc" : "#ff6b35"}
              stroke="#ffffff"
              strokeWidth={1}
              style={{ cursor: 'move' }}
            />
          ))}
        </>
      )}
    </g>
  );
}); 